import React from "react";
import { Badge, Dropdown, Table } from "@optimizely/axiom";

import { BulkUpload } from "../../../domain/BulkUpload";
import { BulkUploadResultStatus } from "../../../constants/constants";
import {
    EMPTY_VALUE,
    formatProgress,
    formatTimestamp,
    getResultBadgeColor,
    getResultBadgeLabel
} from "../../../lib/bulk-upload-format";
import { getFormattedDuration } from "../../../lib/date-formatter";
import { MoreMenu } from "../MoreMenu/MoreMenu";
import { TableSkeleton } from "../Skeleton/TableSkeleton";

/**
 * Renders the result badge for an upload. `resultStatus` is absent until the downstream job has
 * processed the file, so unprocessed rows fall back to a placeholder.
 */
const ResultBadge = ({ resultStatus }: { resultStatus?: BulkUploadResultStatus }) => {
    if (!resultStatus) {
        return <>{EMPTY_VALUE}</>;
    }

    return <Badge color={getResultBadgeColor(resultStatus)}>{getResultBadgeLabel(resultStatus)}</Badge>;
};

/**
 * How long processing took, from the job's own start/end stamps. Both are absent until the
 * blob-trigger job records them — a queued upload has neither and an in-flight one has only a
 * start — so anything without a completed span falls back to a placeholder.
 */
const processingDuration = ({ startTime, endTime }: BulkUpload): string =>
    getFormattedDuration({ start: startTime, end: endTime }) ?? EMPTY_VALUE;

export const BulkUploadsTable = ({
    uploads,
    isLoading,
    error,
    onUploadClick,
    selectedFileIdentifier
}: {
    uploads: BulkUpload[];
    isLoading: boolean;
    error: boolean;
    onUploadClick: ({ upload }: { upload: BulkUpload }) => void;
    selectedFileIdentifier?: string;
}) => {
    if (isLoading) {
        return (
            <div className="push-double--top">
                <TableSkeleton fullWidth />
            </div>
        );
    }

    if (error) {
        return <div data-testid="bulk-uploads-error">Failed to load uploads. Please try again later.</div>;
    }

    return (
        <Table density="loose" data-testid="bulk-uploads-table">
            <Table.THead>
                <Table.TR>
                    <Table.TH sorting={{}} width="20%">
                        File Name
                    </Table.TH>
                    <Table.TH sorting={{}} width="16%">
                        Uploaded By
                    </Table.TH>
                    <Table.TH sorting={{}} width="15%">
                        Uploaded
                    </Table.TH>
                    <Table.TH sorting={{}} width="11%">
                        Status
                    </Table.TH>
                    <Table.TH sorting={{}} width="11%">
                        Result
                    </Table.TH>
                    <Table.TH sorting={{}} width="10%">
                        Success
                    </Table.TH>
                    <Table.TH sorting={{}} width="12%">
                        Duration
                    </Table.TH>
                    <Table.TH sorting={{}} width="5%"></Table.TH>
                </Table.TR>
            </Table.THead>
            <Table.TBody>
                {uploads.map((upload) => {
                    const {
                        fileIdentifier,
                        originalFileName,
                        uploaderEmail,
                        uploadedAt,
                        status,
                        resultStatus,
                        successCount,
                        recordCount
                    } = upload;

                    return (
                        <Table.TR
                            key={fileIdentifier}
                            data-testid={`bulk-upload-${fileIdentifier}-row`}
                            data-cy={`bulk-upload-${fileIdentifier}-row`}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event: React.KeyboardEvent) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    onUploadClick({ upload });
                                }
                            }}
                            onClick={(event: React.MouseEvent) => {
                                // The more menu lives inside the row, so without this its own
                                // clicks would also open the sidebar behind it.
                                const target = event.target as HTMLElement;
                                if (
                                    target.closest('[data-cy="more-menu"]') ||
                                    target.closest('[data-cy="more-menu-contents"]')
                                ) {
                                    return;
                                }
                                onUploadClick({ upload });
                            }}
                            backgroundColor={fileIdentifier === selectedFileIdentifier ? "light" : "faint"}
                        >
                            <Table.TD className="link">{originalFileName}</Table.TD>
                            <Table.TD>{uploaderEmail}</Table.TD>
                            <Table.TD>{formatTimestamp(uploadedAt)}</Table.TD>
                            <Table.TD>{status}</Table.TD>
                            <Table.TD>
                                <ResultBadge resultStatus={resultStatus} />
                            </Table.TD>
                            <Table.TD data-testid={`bulk-upload-${fileIdentifier}-success`}>
                                {formatProgress(successCount, recordCount)}
                            </Table.TD>
                            <Table.TD>{processingDuration(upload)}</Table.TD>
                            <Table.TD>
                                <MoreMenu>
                                    <Dropdown.ListItem>
                                        <Dropdown.BlockLink
                                            data-cy="view-upload-details-action"
                                            onClick={() => onUploadClick({ upload })}
                                        >
                                            View details
                                        </Dropdown.BlockLink>
                                    </Dropdown.ListItem>
                                </MoreMenu>
                            </Table.TD>
                        </Table.TR>
                    );
                })}
            </Table.TBody>
        </Table>
    );
};

BulkUploadsTable.displayName = "BulkUploadsTable";
